import React, { useEffect, useState } from 'react';

// Components
import { SMPContainer, SMPFlex, SMPText, SMPTooltip, styled } from '../../../index';
import { Tooltip } from '../Tooltip';
import { TableRows } from '../TableRows';

// Hooks
import useResizeObserver from '../../../../hooks/useResizeObserver';

// Types
import { IHeadLabel, INormalizedStanding } from '../../../../types';
import { CSS } from '../../../../theme/stitches.config';
import { Sports, TableVariants, validForCustomSportsStyles } from '../../../../constants/sportType';
import { StandingsTableRows } from '../StandingsTableRows';
import { StatsTableRows } from '../StatsTableRows';

export interface ITableProps {
  groupName?: string;
  handleClick?: (id: string | number, name: string) => void;
  handleTeamClick?: (id: string | number, name: string) => void;
  rows?: INormalizedStanding[];
  columns?: IHeadLabel[];
  borderedLogo?: boolean;
  addCustomStyles?: boolean;
  noDataMessage?: string;
  sport?: string;
  isMobile?: boolean;
  isTeamSquad?: boolean;
  position?: string;
  displayImage?: boolean;
  tableVariant?: TableVariants;
}

const View = styled.withConfig({ componentId: 'CommonWidget', displayName: 'TableView' })('div', {
  position: 'relative',
  margin: 'auto',
  width: '100%',
});

const Wrapper = styled.withConfig({ componentId: 'CommonWidget', displayName: 'TableWrapper' })('div', {
  position: 'relative',
  whiteSpace: 'nowrap',

  '@bp0': {
    overflow: 'auto',
    zIndex: '20',
  },

  '@bp1': {
    overflow: 'visible',
  },

  // The following lines allow side scrolling while the scrollbar is hidden
  // Chrome/Safari/Webkit
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  // Firefox
  scrollbarWidth: 'none',
  // IE 10+
  msOverflowStyle: 'none',
});

const StickyColH = styled.withConfig({ componentId: 'CommonWidget', displayName: 'StickyColH' })('th', {
  position: 'sticky',
  bc: '$primaryBackgroundColor',
  left: '0',
});

const StandardColH = styled.withConfig({ componentId: 'CommonWidget', displayName: 'StandardColH' })('th', {
  minWidth: '$2_5',
});

export const Table = ({
  groupName,
  handleClick,
  rows,
  columns,
  addCustomStyles,
  handleTeamClick,
  noDataMessage,
  sport,
  isMobile,
  isTeamSquad,
  position,
  displayImage,
  tableVariant,
}: ITableProps): JSX.Element => {
  const [containerSize, setContainerSize] = useState<undefined | number>(undefined);
  const [isSmallSize, setIsSmallSize] = useState(true);
  const [forceTooltipRender, setForceTooltipRender] = useState(true); // Workaround for a Stitches rendering bug

  useEffect(() => {
    setForceTooltipRender(false);
  }, [isSmallSize]);

  const measuredContainerRef = useResizeObserver((container) => {
    setIsSmallSize(container.offsetWidth < 768);
    setContainerSize(container.offsetWidth);
  });

  // Determine if the current group is "goalkeeper"
  const isGoalkeeperGroup = position === 'keeper';

  // Function to determine if a column should be rendered based on groupName and column value
  const shouldRenderColumn = (column: IHeadLabel) => {
    if (isGoalkeeperGroup) {
      return true; // Render all columns for goalkeeper group
    }
    // Exclude 'conceded' columns for non-goalkeeper groups
    return column.statValue !== 'conceded';
  };

  const toggleTooltip = (e: React.PointerEvent<HTMLSpanElement>, show: boolean) => {
    if (!(e.target instanceof Element)) {
      return;
    }

    const statContainer: HTMLDivElement | null = (e.target as Element).closest('.stat-column');
    const tooltip: HTMLDivElement | null | undefined = statContainer?.querySelector('.stat-column-tooltip');

    if (tooltip) {
      try {
        tooltip.style.display = show ? 'block' : 'none';
      } catch (e) {
        console.error(e);
      }
    }
  };

  const applyCustomCssForStandings = validForCustomSportsStyles.includes(sport || '');
  const applyCustomCss = tableVariant === TableVariants.STATS || applyCustomCssForStandings;
  const tableHeaderColCss = applyCustomCss
    ? {
        paddingTop: '10px',
        paddingBottom: '10px',
        height: '20px',
      }
    : {};

  const tableStickyHeaderColCss = applyCustomCss
    ? {
        paddingLeft: '16px',
      }
    : {};

  return (
    <SMPContainer
      ref={measuredContainerRef}
      variant='paddinglessContainer'
      css={{
        mb: isTeamSquad ? undefined : '$1',
      }}
    >
      {groupName && (
        <SMPFlex variant='standingsMarginals' style={{ backgroundColor: isTeamSquad ? undefined : 'var(--colors-subHeaderBgColor)' }}>
          <SMPText variant='contrast' size={`${applyCustomCss ? 3 : 2}`} weight='700'>
            {groupName}
          </SMPText>
        </SMPFlex>
      )}
      <View>
        <Wrapper
          style={{ overflow: isMobile && applyCustomCssForStandings ? 'hidden' : isSmallSize ? 'auto' : '' }}
          css={{
            // This controls the padding of Standings table cells
            '& th:not(:first-child) > :first-child, & td:not(:first-child) > :first-child': {
              mr: isMobile && !applyCustomCssForStandings ? '$2' : undefined,
            },
          }}
        >
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <StickyColH
                  style={{
                    ...tableStickyHeaderColCss,
                    ...tableHeaderColCss,
                    backgroundColor: 'var(--colors-subHeaderBgColor)',
                  }}
                  css={{
                    '@bp0': { zIndex: '20' },
                    minWidth: isSmallSize ? '' : '200px',
                    '@bp2': { zIndex: isSmallSize ? '20' : '0' },
                  }}
                >
                  <SMPFlex
                    css={
                      applyCustomCss
                        ? { justifyContent: 'left' }
                        : {
                            py: '$2',
                            justifyContent: 'left',
                            ml: '$2',
                          }
                    }
                  >
                    <SMPText
                      variant='contrast'
                      style={{
                        width: `${addCustomStyles ? '24px' : '16px'}`,
                        marginRight: `${applyCustomCssForStandings ? (isMobile ? '10px' : '16px') : '16px'}`,
                        fontWeight: applyCustomCssForStandings ? '600' : undefined,
                      }}
                      size={`${applyCustomCss ? 3 : 2}`}
                    >
                      #
                    </SMPText>
                    {columns
                      ? columns.slice(0, 1).map((label: IHeadLabel, index: number) => {
                          const css =
                            sport === Sports.FOOTBALL
                              ? {
                                  minWidth: '120px',
                                  maxWidth: '175px',
                                }
                              : { minWidth: '160px' };
                          return (
                            <React.Fragment key={`sticky - col - ${index}`}>
                              {label.tooltip && (forceTooltipRender || !isSmallSize) ? (
                                <Tooltip text={label.tooltip}>
                                  <SMPText
                                    variant='contrast'
                                    size='2'
                                    css={{
                                      textAlign: 'left',
                                      flexGrow: '1',
                                      ...css,
                                    }}
                                  >
                                    {label?.value}
                                  </SMPText>
                                </Tooltip>
                              ) : (
                                <SMPText
                                  variant='contrast'
                                  size={`${applyCustomCss ? 3 : 2}`}
                                  style={{ fontWeight: applyCustomCssForStandings ? '600' : undefined }}
                                  css={{
                                    textAlign: 'left',
                                    flexGrow: '1',
                                    userSelect: 'none',
                                    ...css,
                                  }}
                                >
                                  {label?.value}
                                </SMPText>
                              )}
                            </React.Fragment>
                          );
                        })
                      : null}
                  </SMPFlex>
                </StickyColH>
                {columns
                  ? columns.slice(1, columns.length).map((label: IHeadLabel, index: number) => {
                      if (!shouldRenderColumn(label)) return null; // Conditionally render columns based on groupName and column value
                      return (
                        <StandardColH
                          style={tableHeaderColCss}
                          key={`col - ${index}`}
                          className='stat-column'
                          css={{
                            bc: '$subHeaderBgColor',
                            paddingRight: applyCustomCss && index === columns.length - 2 ? '16px' : undefined, // Add right padding to the last StandardColH element
                            gap: applyCustomCss ? '4px' : undefined,
                          }}
                          onClick={(e) => e.preventDefault()}
                          onPointerEnter={(e) => !isSmallSize && toggleTooltip(e, true)}
                          onPointerLeave={(e) => !isSmallSize && toggleTooltip(e, false)}
                        >
                          <div style={{ position: 'relative' }}>
                            <SMPTooltip
                              css={{
                                bottom: '100%',
                                marginBottom: '10px',
                                transition: 'display 0.5s',
                              }}
                              className='stat-column-tooltip'
                            >
                              <span>{label?.tooltip}</span>
                            </SMPTooltip>
                          </div>
                          <SMPText
                            variant='contrast'
                            size={`${applyCustomCss ? 3 : 2}`}
                            css={
                              label.css
                                ? (label.css as CSS)
                                : ({
                                    width: '$6',
                                    textAlign: 'center',
                                    userSelect: 'none',
                                  } as CSS)
                            }
                          >
                            {label?.value}
                          </SMPText>
                        </StandardColH>
                      );
                    })
                  : null}
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Define the components to be rendered based on conditions
                const components = {
                  STANDINGS_TABLE: (
                    <StandingsTableRows
                      rows={rows}
                      columnsLength={columns?.length ?? 0}
                      isSmallSize={isSmallSize}
                      containerSize={containerSize}
                      handleClick={handleClick}
                      handleTeamClick={handleTeamClick}
                      noDataMessage={noDataMessage}
                      displayImage={displayImage}
                    />
                  ),
                  DEFAULT: (
                    <TableRows
                      rows={rows}
                      columnsLength={columns?.length ?? 0}
                      isSmallSize={isSmallSize}
                      handleClick={handleClick}
                      handleTeamClick={handleTeamClick}
                      noDataMessage={noDataMessage}
                    />
                  ),
                  STATS_TABLE: (
                    <StatsTableRows
                      rows={rows}
                      columnsLength={columns?.length ?? 0}
                      isSmallSize={isSmallSize}
                      handleClick={handleClick}
                      handleTeamClick={handleTeamClick}
                      noDataMessage={noDataMessage}
                      containerSize={containerSize}
                      displayImage={displayImage}
                    />
                  ),
                };

                switch (tableVariant) {
                  case TableVariants.STANDINGS:
                    return components.STANDINGS_TABLE;
                  case TableVariants.STATS:
                    return components.STATS_TABLE;
                  default:
                    return components.DEFAULT;
                }
              })()}
            </tbody>
          </table>
        </Wrapper>
      </View>
    </SMPContainer>
  );
};
